/**
 * SoliticsPopupDelegate is responsible for handling various popup events, such as deciding if a popup should open,
 * handling the display and dismissal of the popup, and handling actions triggered from the popup.
 */
export class SoliticsPopupDelegate {
    // Constructor with typed parameters
    /**
     * Constructor for initializing the SoliticsPopupDelegate with custom callbacks for popup events.
     *
     * @param {Object} params - Object containing optional callback functions for various popup events.
     * @param {Function} [params.onPopupShouldOpenEventCallback] - A callback function to determine if a popup should open.
     * @param {Function} [params.onPopupOpenedCallback] - A callback function called when the popup has been opened.
     * @param {Function} [params.onPopupClosedCallback] - A callback function called when the popup has been closed.
     * @param {Function} [params.onPopupClick_Callback] - A callback function called when an action is triggered from the popup.
     * @param {Function} [params.onShouldDismissForNavEventCallback] - A callback function to decide if the popup should dismiss for a navigation target.
     * @param {Function} [params.onPopupClosedForNavigation] - A callback function triggered after the popup has been dismissed for a navigation target.
     * @param {Function} [params.onPopupFailedToOpenCallback] - A callback function triggered when the popup fails to open.
     */
    constructor({ onPopupShouldOpenEventCallback, onPopupOpenedCallback, onPopupClosedCallback, onPopupClick_Callback, onShouldDismissForNavEventCallback, onPopupClosedForNavigation, onPopupFailedToOpenCallback, } = {}) {
        // Private fields
        /**
         * Callback function to decide if a popup message should be opened.
         * @param data - The content of the popup message.
         * @returns A boolean indicating if the popup should open (true) or not (false).
         * Default is true.
         */
        this._onPopupShouldOpenEventCallback = () => true;
        /**
         * Callback function called when the popup message has been displayed.
         */
        this._onPopupOpenedEventCallback = () => { };
        /**
         * Callback function called when the popup message has been dismissed.
         */
        this._onPopupClosedEventCallback = () => { };
        /**
         * Callback function called when an action has been triggered from the popup message.
         */
        this._onPopupClick_EventCallback = () => { };
        /**
         * Callback function to decide if the popup should dismiss for a specific navigation target.
         * @param url - The URL of the navigation target.
         * @returns A boolean indicating if the popup should be dismissed for the given navigation target (true) or not (false).
         * Default is true.
         */
        this._onShouldDismissForNavEventCallback = () => true;
        /**
         * Callback function triggered after the popup has been dismissed for a specific navigation target.
         * @param url - The URL string of the navigation target after the popup is dismissed.
         */
        this._onPopupClosedForNavigation = () => { };
        /**
         * Callback function triggered when the popup fails to open.
         * @param data - The content of the popup message that failed to open.
         */
        this._onPopupFailedToOpenCallback = (_) => { };
        if (onPopupShouldOpenEventCallback) {
            this._onPopupShouldOpenEventCallback = onPopupShouldOpenEventCallback;
        }
        if (onPopupOpenedCallback) {
            this._onPopupOpenedEventCallback = onPopupOpenedCallback;
        }
        if (onPopupClosedCallback) {
            this._onPopupClosedEventCallback = onPopupClosedCallback;
        }
        if (onPopupClick_Callback) {
            this._onPopupClick_EventCallback = onPopupClick_Callback;
        }
        if (onShouldDismissForNavEventCallback) {
            this._onShouldDismissForNavEventCallback = onShouldDismissForNavEventCallback;
        }
        if (onPopupClosedForNavigation) {
            this._onPopupClosedForNavigation = onPopupClosedForNavigation;
        }
        if (onPopupFailedToOpenCallback) {
            this._onPopupFailedToOpenCallback = onPopupFailedToOpenCallback;
        }
    }
    // Getter methods to access private properties
    /**
     * Returns the callback function to determine if a popup should open.
     * @returns The function that checks if the popup should open.
     */
    getOnPopupShouldOpenEventCallback() {
        return this._onPopupShouldOpenEventCallback;
    }
    /**
     * Returns the callback function called when the popup has been displayed.
     * @returns The function triggered when the popup is opened.
     */
    getOnPopupOpenedEventCallback() {
        return this._onPopupOpenedEventCallback;
    }
    /**
     * Returns the callback function called when the popup has been dismissed.
     * @returns The function triggered when the popup is closed.
     */
    getOnPopupClosedEventCallback() {
        return this._onPopupClosedEventCallback;
    }
    /**
     * Returns the callback function called when an action is triggered from the popup.
     * @returns The function triggered when an action is clicked in the popup.
     */
    getOnPopupClick_EventCallback() {
        return this._onPopupClick_EventCallback;
    }
    /**
     * Returns the callback function to determine if the popup should dismiss for a navigation target.
     * @param url - The URL of the navigation target.
     * @returns The function that checks if the popup should dismiss for a given URL.
     */
    getOnShouldDismissForNavEventCallback() {
        return this._onShouldDismissForNavEventCallback;
    }
    /**
     * Returns the callback function triggered after the popup has been dismissed for a navigation target.
     * @param url - The URL of the navigation target after the popup is dismissed.
     * @returns The function triggered after the popup is dismissed for the given URL.
     */
    getOnPopupClosedForNavigation() {
        return this._onPopupClosedForNavigation;
    }
    /**
     * Returns the callback function triggered when the popup fails to open.
     * @param data - The content of the popup message that failed to open.
     * @returns The function triggered when the popup fails to open.
     */
    getOnPopupFailedToOpenCallback() {
        return this._onPopupFailedToOpenCallback;
    }
}
;
